import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { format, startOfDay, endOfDay, differenceInMinutes, addMinutes, isValid } from 'date-fns';
import { useMemo } from 'react';

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface TimeOption {
  value: string;
  label: string;
}

interface TimeSelectProps extends Omit<ComponentProps<typeof SelectTrigger>, 'value'> {
  value: Date | null;
  onValueChange: (value: Date) => void;
  showIcon?: boolean;
  className?: string;
  placeholder?: string;
  minutesStep?: number;
  startDate?: Date;
  endDate?: Date;
}

export default function TimeSelect(timeSelectProps: TimeSelectProps) {
  const {
    value,
    onValueChange,
    placeholder = 'Wybierz',
    minutesStep = 15,
    startDate = startOfDay(new Date()),
    endDate = endOfDay(new Date()),
    showIcon = false,
    className,
    ...props
  } = timeSelectProps;
  const options = useMemo<Array<TimeOption>>(() => {
    const difference = differenceInMinutes(endDate, startDate);
    const stepsInDifference = Math.floor(difference / minutesStep);
    return Array.from({ length: stepsInDifference }, (_, i) => {
      const minutes = i * minutesStep;
      const dayOffset = Math.floor(minutes / (24 * 60));
      const dateTime = addMinutes(startDate, minutes);
      return {
        value: dateTime.toISOString(),
        label: `${format(dateTime, 'HH:mm')} ${dayOffset > 0 ? `+${dayOffset}d` : ''}`,
      };
    });
  }, [startDate, endDate, minutesStep]);

  const selectedOption = useMemo<TimeOption | null>(() => {
    if (value == null || !isValid(value)) return null;
    const iso = value.toISOString();
    return options.find((opt) => opt.value === iso) ?? null;
  }, [value, options]);

  const handleValueChange = (newValue: string) => {
    const date = new Date(newValue);
    if (isValid(date)) onValueChange(date);
  };

  return (
    <Select value={selectedOption?.value ?? ''} onValueChange={handleValueChange}>
      <SelectTrigger showIcon={showIcon} className={cn('min-w-15 px-1 text-sm justify-center', className)} {...props}>
        <SelectValue placeholder={placeholder} aria-label={value?.toString()}>
          {selectedOption?.label ?? ''}
        </SelectValue>
      </SelectTrigger>
      <SelectContent position="popper">
        <SelectGroup>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
